import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { BaseComponent } from '~/pages/base.component';
import { HttpService } from '~/core/services/http.service';
import { AppSettingsService } from '~/core/services/appsettings.service';
import { FormatterService } from '~/core/services/formatter.service';
import { WellsApiService } from '~/services/wells-api.service';
import { TabNavigationService } from '~/services/tab-navigation.service';
import { WellViewMappingService } from '~/services/wellview-mapping.service';
import { NotificationService } from '~/services/notification.service';
import { UnitConversionService } from '~/services/unit-conversion.service';
import { ConfirmationService } from '~/services/confirmation.service';
import { CanComponentDeactivate } from '~/core/classes/auth/pending-changes.guard';

@Component({
    selector: 'app-well-detail',
    templateUrl: './well-detail.component.html',
    styleUrls: ['./well-detail.component.scss'],
    standalone: false
})
export class WellDetailComponent extends BaseComponent implements OnInit, OnDestroy, CanComponentDeactivate {
    public wellName: string = '';  // This is actually the wellIda (API Number)
    public bu: string = '';  // Business Unit (RBU or ABU)
    public schemas: any[] = [];
    public processedSchemas: any[] = [];
    public stagesCompleted: string[] = [];  // Stages completed from API
    public selectedSchema: string = '';
    public selectedSchemaApiName: string = '';
    public schemaData: any = null;
    public schemaDataSections: any[] = [];
    public displayKeys: string[] = [];
    public loading: boolean = false;
    public loadingSchemas: boolean = false;
    public loadingSchemaData: boolean = false;
    public showReview: boolean = false;
    public reviewData: any = { changedRecords: [] };
    
    // Schema ordering configuration for ABU
    private readonly SCHEMA_ORDER_ABU = [
        'Well Header',
        'Wellbore',
        'Wellbore Section',
        'Geological Horizon',
        'Tubing',
        'Tubing Components',
        'Rod',
        'Rod Components',
        'Casing',
        'Casing Components',
        'Cement',
        'Cement Stage',
        'Perforation',
        'Other in Hole'
    ];
    
    // Mapping from API schema names to display names for ABU
    private readonly SCHEMA_NAME_MAP_ABU: { [key: string]: string } = {
        'cas': 'Casing',
        'casing': 'Casing',
        'cascomp': 'Casing Components',
        'casing_components': 'Casing Components',
        'casingcomponents': 'Casing Components',
        'cement': 'Cement',
        'cementstage': 'Cement Stage',
        'cement_stage': 'Cement Stage',
        'perforation': 'Perforation',
        'tubing': 'Tubing',
        'tubing_assembly': 'Tubing',
        'tubingcomp': 'Tubing Components',
        'tubing_components': 'Tubing Components',
        'tubingcomponents': 'Tubing Components',
        'rod': 'Rod',
        'rod details': 'Rod',
        'rodcomp': 'Rod Components',
        'rod_components': 'Rod Components',
        'rodcomponents': 'Rod Components',
        'wellheader': 'Well Header',
        'well_header': 'Well Header',
        'wellbore': 'Wellbore',
        'wellboresz': 'Wellbore Section',
        'wellbore_sz': 'Wellbore Section',
        'wellboresection': 'Wellbore Section',
        'wellboreformation': 'Geological Horizon',
        'wellbore_formation': 'Geological Horizon',
        'otherinhole': 'Other in Hole',
        'other_in_hole': 'Other in Hole',
    };
    
    // Schema ordering configuration for RBU
    private readonly SCHEMA_ORDER_RBU = [
        'Well Header',
        'Wellbore',
        'Wellbore Section',
        'Geological Horizon',
        'Jobs',
        'Casing',
        'Casing Components',
        'Cement',
        'Cement Stage',
        'Perforation',
        'Other in Hole'
    ];
    
    // Dropdown options for specific fields
    private readonly DROPDOWN_OPTIONS: { [schemaName: string]: { [fieldName: string]: string[] } } = {
        'Casing': {
            'Item Description': [
                'Structural',
                'Conductor',
                'Surface',
                'Intermediate Casing 1',
                'Intermediate Casing 2',
                'Intermediate Casing 3',
                'Drilling Tieback',
                'Drilling Liner 1',
                'Drilling Liner 2',
                'Drilling Liner 3',
                'Drilling Liner 4',
                'Drilling Liner 5',
                'Drilling Liner 6',
                'Drilling Liner 7',
                'Production Casing',
                'Production Liner 1',
                'Production Liner 2',
                'Production Liner 3',
                'Production Tieback'
            ]
        },
        'Cement': {
            'Cement Description': [
                'Top Job',
                'Tieback Casing Cement',
                'Surface Casing Cement',
                'Production Tubing Cement',
                'Production Casing Cement',
                'Perforation Squeeze',
                'Liner Cement',
                'Intermediate Casing Cement',
                'Conductor Cement',
                'Cement Squeeze',
                'Cement Plug',
                'Cement Alternative Plug',
                'Balanced Plug (Tubing and Annulus)'
            ]
        }
    };
    
    // Mapping from API schema names to display names for RBU
    private readonly SCHEMA_NAME_MAP_RBU: { [key: string]: string } = {
        'cas': 'Casing',
        'casing': 'Casing',
        'cascomp': 'Casing Components',
        'casing_components': 'Casing Components',
        'casingcomponents': 'Casing Components',
        'cement': 'Cement',
        'cementstage': 'Cement Stage',
        'cement_stage': 'Cement Stage',
        'perforation': 'Perforation',
        'job': 'Jobs',
        'jobs': 'Jobs',
        'wellheader': 'Well Header',
        'well_header': 'Well Header',
        'wellbore': 'Wellbore',
        'wellboresz': 'Wellbore Section',
        'wellbore_sz': 'Wellbore Section',
        'wellboresection': 'Wellbore Section',
        'wellboreformation': 'Geological Horizon',
        'wellbore_formation': 'Geological Horizon',
        'geological_horizon': 'Geological Horizon',
        'otherinhole': 'Other in Hole',
        'other_in_hole': 'Other in Hole',
    };
    
    private destroy$ = new Subject<void>();
    
    // Well Files properties
    public selectedDocType: string = '';  // For ABU: doc type (ddr, dtr, wss); For RBU: file_name
    public fileAvailability: any = null;
    public loadingWellFiles: boolean = false;
    public wellFiles: any[] = [];  // For RBU: stores the selected file object
    
    // Schema data properties
    public sectionEditMode: boolean[] = [];
    public sectionHasChanges: boolean[] = [];
    public isSaving: boolean = false;
    public fieldTypeMap: { [key: string]: string } = {};  // Map field names to their data types
    
    // Image preview properties
    public selectedImageUrl: string = '';
    public loadingImage: boolean = false;
    public previewError: string = '';
    public previewDocumentType: string = 'image';
    public previewDocumentTitle: string = '';
    public selectedFieldPolygon: any = null;
    public imageZoomLevel: number = 100;
    public minZoom: number = 50;
    public maxZoom: number = 200;
    
    // Logs properties
    public suggestionsLogs: any[] = [];
    public loadingLogs: boolean = false;
    public showLogsTable: boolean = false;
    
    // Confirmation dialogs
    public showDeleteConfirmation: boolean = false;
    public showSaveConfirmation: boolean = false;
    public showEditCancelConfirmation: boolean = false;
    public recordToDelete: number = -1;  // Index of record pending deletion
    public sectionIndexToCancel: number = -1;  // Index of section with unsaved changes being cancelled
    
    // Notification properties
    error: string = '';
    notificationType: 'success' | 'error' | 'info' = 'info';
    
    // Image properties
    imageViewBox: string = '';
    
    // Bulk Ingest properties
    public showBulkIngestConfirmation: boolean = false;

    // Fill Comments popup properties (used for bulk ingest preparation)
    public showFillCommentsPopup: boolean = false;
    private fillCommentsForBulkIngest: boolean = false;  // Flag to track bulk ingest context
    
    constructor(
        public httpService: HttpService,
        public appSettingsService: AppSettingsService,
        public formatterService: FormatterService,
        public wellsApiService: WellsApiService,
        private route: ActivatedRoute,
        private router: Router,
        private tabNavigationService: TabNavigationService,
        private wellViewMappingService: WellViewMappingService,
        private notificationService: NotificationService,
        private unitConversionService: UnitConversionService,
        private confirmationService: ConfirmationService
    ) {
        super();
    }
    
    async ngOnInit(): Promise<void> {
        // BU check is handled by BuSelectionGuard, so we can safely continue here
        await super.ngOnInit();
        
        // Load WellView mappings cache
        this.wellViewMappingService.getMappings().subscribe({
            next: (mappings) => {
                console.log('[ngOnInit] WellView mappings loaded successfully', mappings);
            },
            error: (err) => {
                console.error('[ngOnInit] Error loading WellView mappings:', err);
            }
        });
        
        // Get BU from sessionStorage
        this.bu = sessionStorage.getItem('selectedBU') || 'ABU';
        
        // Get stages_completed from navigation service (can be empty for new/pending wells)
        this.stagesCompleted = this.tabNavigationService.getStagesCompleted();
        if (!this.stagesCompleted || !Array.isArray(this.stagesCompleted)) {
            this.stagesCompleted = [];
        }
        console.log('[ngOnInit] Stages completed:', this.stagesCompleted);
        
        // Get well name from route params
        this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
            if (params['wellName']) {
                this.wellName = params['wellName'];
                
                // Check if page was refreshed
                // stagesCompleted is only stored in-memory in TabNavigationService
                // It's NOT persisted in localStorage/sessionStorage
                // So if it's empty, the user might have refreshed the page and lost the navigation context
                
                // However, skip this check if we're in a valid navigation flow
                // (e.g., coming back from review, where we just fetched and set stages_completed)
                const isValidNavigation = this.tabNavigationService.isValidNavigationFlow();
                
                if (!isValidNavigation && (!this.stagesCompleted || this.stagesCompleted.length === 0)) {
                    console.warn('[ngOnInit] Page refresh detected (stagesCompleted missing). Redirecting to /wells');
                    this.router.navigate(['/wells']);
                    return;
                }
                
                // Clear prepareMode flag when navigating to well detail normally
                // This ensures prepareMode only affects the prepare page
                try {
                    localStorage.removeItem('prepareMode');
                    console.log('[ngOnInit] Cleared prepareMode flag');
                } catch (e) {
                    console.warn('[ngOnInit] Could not clear prepareMode from localStorage');
                }
                
                // Load schemas and files
                this.loadSchemas();
                this.loadWellFileAvailability();
            }
        });
        
        // Check if we're in review mode
        this.route.url.pipe(takeUntil(this.destroy$)).subscribe(url => {
            this.showReview = url.some(segment => segment.path === 'review');
        });
        
        // Initialize navigation guard - allow navigation by default
        sessionStorage.removeItem('__allowNavigate__');
        
        // Initialize navigation guard - allow navigation by default
        sessionStorage.removeItem('__allowNavigate__');
    }
    
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        // Clear the navigation guard flag when leaving this component
        sessionStorage.removeItem('__allowNavigate__');
        // Clear the navigation guard flag when leaving this component
        sessionStorage.removeItem('__allowNavigate__');
        // Don't clear stages here - they should persist after navigating back from review
    }
    
    /**
     * Update the navigation guard flag based on current editing/saving state
     * Sets to "false" if there are unsaved changes (prevents navigation)
     * Sets to "true" if all changes are saved (allows navigation)
     */
    private updateNavigationGuard(): void {
        // Check if there are any unsaved changes
        const hasUnsavedChanges = this.hasAnyChanges() || this.isAnyEditModeActive();
        
        if (hasUnsavedChanges) {
            sessionStorage.setItem('__allowNavigate__', 'false');
        } else {
            sessionStorage.removeItem('__allowNavigate__');
        }
    }
    
    
    public loadSchemas(): void {
        this.loadingSchemas = true;
        this.wellsApiService.getSchemaList(this.wellName, this.bu).subscribe({
            next: (response: any) => {
                // Handle both array and object responses
                this.schemas = Array.isArray(response) ? response : (response.schemas || []);
                this.processAndOrderSchemas();
                this.loadingSchemas = false;
                
                // Try to restore previously selected schema from service
                const savedSchema = this.tabNavigationService.getSelectedSchema();
                const savedSchemaApiName = this.tabNavigationService.getSelectedSchemaApiName();
                
                // If we have a saved schema, use it (regardless of whether we're in review mode)
                // This ensures schema selection from approval flow navigation works
                if (savedSchema && savedSchemaApiName) {
                    const savedSchemaExists = this.processedSchemas.find(s => s.displayName === savedSchema && s.enabled);
                    if (savedSchemaExists) {
                        this.selectedSchema = savedSchema;
                        this.selectedSchemaApiName = savedSchemaApiName;
                        this.loadSchemaData(savedSchemaApiName);
                        
                        // Clear the saved schema after using it (so it doesn't persist on next navigation)
                        this.tabNavigationService.clearSelectedSchema();
                        return;
                    }
                }
                
                // Otherwise, automatically select the first enabled schema
                const firstEnabledSchema = this.processedSchemas.find(s => s.enabled);
                if (firstEnabledSchema) {
                    this.selectedSchema = firstEnabledSchema.displayName;
                    this.selectedSchemaApiName = firstEnabledSchema.apiName;
                    this.loadSchemaData(firstEnabledSchema.apiName);
                }
            },
            error: (err: any) => {
                console.error('Error loading schemas:', err);
                this.notificationService.error('Failed to load schemas: ' + (err.error?.detail || err.message || 'Unknown error'));
                this.loadingSchemas = false;
            }
        });
    }
    
    private processAndOrderSchemas(): void {
        // Get selected BU from sessionStorage
        const selectedBU = sessionStorage.getItem('selectedBU') || 'ABU';
        
        // Select appropriate schema order and name map based on BU
        const SCHEMA_ORDER = selectedBU === 'RBU' ? this.SCHEMA_ORDER_RBU : this.SCHEMA_ORDER_ABU;
        const SCHEMA_NAME_MAP = selectedBU === 'RBU' ? this.SCHEMA_NAME_MAP_RBU : this.SCHEMA_NAME_MAP_ABU;
        
        // Normalize API schema names
        const normalizedApiSchemas = this.schemas.map(schema => {
            const schemaName = typeof schema === 'string' ? schema : (schema.name || '');
            return schemaName.toLowerCase().replace(/[_\s]+/g, '_');
        });
        
        // Create a set of available display names from API response
        const availableDisplayNames = new Set<string>();
        
        // Map API schemas to display names
        for (const apiSchema of this.schemas) {
            const schemaName = typeof apiSchema === 'string' ? apiSchema : (apiSchema.name || '');
            const normalizedName = schemaName.toLowerCase().replace(/[_\s]+/g, '_');
            const originalLower = schemaName.toLowerCase();
            
                        
            // Try to find in map: first check normalized version, then original with spaces/underscores
            let displayName = SCHEMA_NAME_MAP[normalizedName] || SCHEMA_NAME_MAP[originalLower] || this.formatSchemaName(schemaName);
            availableDisplayNames.add(displayName);
            console.log(`[processAndOrderSchemas] API schema: "${schemaName}" -> normalized: "${normalizedName}", original: "${originalLower}", display: "${displayName}"`);
            console.log(`[processAndOrderSchemas] API schema: "${schemaName}" -> normalized: "${normalizedName}", original: "${originalLower}", display: "${displayName}"`);
        }
        
        // Build ordered schema list
        const orderedSchemas = SCHEMA_ORDER.map(displayName => {
            const enabled = availableDisplayNames.has(displayName);
            
            // Find the original API name for this display name first
            let apiName = displayName.toLowerCase().replace(/\s+/g, '_');
            
            // Check if we have a reverse mapping
            for (const [key, value] of Object.entries(SCHEMA_NAME_MAP)) {
                if (value === displayName) {
                    // Find if this key exists in the API response
                    // Need to check both normalized and original forms of the schema name
                    // Need to check both normalized and original forms of the schema name
                    const matchingSchema = this.schemas.find(schema => {
                        const schemaName = typeof schema === 'string' ? schema : (schema.name || '');
                        const normalized = schemaName.toLowerCase().replace(/[_\s]+/g, '_');
                        const original = schemaName.toLowerCase();
                        // Match if normalized equals key OR original equals key
                        return normalized === key || original === key;
                    });
                    
                    if (matchingSchema) {
                        apiName = typeof matchingSchema === 'string' ? matchingSchema : matchingSchema.name;
                        console.log(`[processAndOrderSchemas] Reverse mapping found: "${displayName}" -> "${apiName}"`);
                        console.log(`[processAndOrderSchemas] Reverse mapping found: "${displayName}" -> "${apiName}"`);
                        break;
                    }
                }
            }
            
            // Check if this schema is completed (now that we have the actual API name)
            const isCompleted = this.isSchemaCompleted(apiName, SCHEMA_NAME_MAP);
            
            return {
                displayName: displayName,
                apiName: apiName,
                enabled: enabled,
                completed: isCompleted
            };
        });
        
        this.processedSchemas = orderedSchemas;
    }
    
    /**
     * Check if a schema is completed based on stages_completed from API
     * Uses the actual API schema name and checks against all possible naming variations
     */
    private isSchemaCompleted(apiSchemaName: string, schemaNameMap: { [key: string]: string }): boolean {
        // Ensure stagesCompleted is always an array before calling .some()
        const stages = Array.isArray(this.stagesCompleted) ? this.stagesCompleted : [];
        
        if (!stages || stages.length === 0) {
            return false;
        }
        
        // Normalize the API schema name (e.g., "Cas", "CasComp", "WellboreSz")
        const normalizedApiName = apiSchemaName.toLowerCase().replace(/[\s_]+/g, '');
        console.log('[isSchemaCompleted] Checking API schema:', apiSchemaName, '-> normalized:', normalizedApiName);
        
        // Check if any stage in stagesCompleted matches this schema
        // We need to handle various naming conventions:
        // API: "Cas" -> stages_completed might have "casing" or "cas"
        // API: "CasComp" -> stages_completed might have "casing_components", "cascomp", etc.
        return stages.some(stage => {
            const normalizedStage = stage.toLowerCase().replace(/[\s_]+/g, '');
            
            // Direct match
            if (normalizedStage === normalizedApiName) {
                console.log('[isSchemaCompleted] Direct match found:', stage);
                return true;
            }
            
            // Check if the stage matches any key in the schema name map that points to the same display name
            // This handles cases like "casing" and "cas" both mapping to "Casing"
            const apiDisplayName = schemaNameMap[normalizedApiName];
            if (apiDisplayName) {
                // Find all keys that map to the same display name
                for (const [key, value] of Object.entries(schemaNameMap)) {
                    if (value === apiDisplayName) {
                        const normalizedKey = key.toLowerCase().replace(/[\s_]+/g, '');
                        if (normalizedStage === normalizedKey) {
                            console.log('[isSchemaCompleted] Mapped match found:', stage, 'via key:', key);
                            return true;
                        }
                    }
                }
            }
            
            return false;
        });
    }
    
    /**
     * Check if the Next button should be enabled for the current schema
     * Sequential unlock: enable only if this is the first schema, already completed, or the next in sequence
     * DISABLED FOR TESTING - Always enabled
     */
    // public isNextButtonEnabled(): boolean {
    //     // Must have a selected schema
    //     if (!this.selectedSchema) {
    //         return false;
    //     }
    //     
    //     // Find current schema in processed list
    //     const currentSchemaIndex = this.processedSchemas.findIndex(s => s.displayName === this.selectedSchema);
    //     if (currentSchemaIndex === -1) {
    //         return false;
    //     }
    //     
    //     // Always enable for first schema (Well Header)
    //     if (currentSchemaIndex === 0) {
    //         return true;
    //     }
    //     
    //     // Always enable if this schema is already completed
    //     if (this.processedSchemas[currentSchemaIndex].completed) {
    //         return true;
    //     }
    //     
    //     // Check if previous schema is completed (sequential unlock)
    //     // Find the previous ENABLED schema in the list
    //     for (let i = currentSchemaIndex - 1; i >= 0; i--) {
    //         if (this.processedSchemas[i].enabled) {
    //             // If the previous enabled schema is completed, enable Next for current
    //             if (this.processedSchemas[i].completed) {
    //                 return true;
    //             }
    //             // If previous enabled schema is NOT completed, disable Next
    //             return false;
    //         }
    //     }
    //     
    //     // No enabled schema before this one, disable
    //     return false;
    // }
    
    /**
     * Get the count of completed schemas
     */
    public getCompletedSchemasCount(): number {
        return this.processedSchemas.filter(s => s.completed && s.enabled).length;
    }
    
    /**
     * Get the progress percentage for the progress bar
     */
    public getProgressPercentage(): number {
        const enabledSchemas = this.processedSchemas.filter(s => s.enabled);
        if (enabledSchemas.length === 0) {
            return 0;
        }
        const completedCount = this.getCompletedSchemasCount();
        return Math.round((completedCount / enabledSchemas.length) * 100);
    }
    
    private formatSchemaName(name: string): string {
        return name
            .replace(/_/g, ' ')
            .replace(/\b\w/g, char => char.toUpperCase());
    }
    
    public onSchemaSelect(schema: any): void {
        if (!schema.enabled || this.loadingSchemaData) return;
        
        // Allow user to select any schema and view data without validation
        // Validation will happen on Next button click
        // Allow user to select any schema and view data without validation
        // Validation will happen on Next button click
        this.selectedSchema = schema.displayName;
        this.selectedSchemaApiName = schema.apiName;
        this.tabNavigationService.setSelectedSchema(schema.displayName, schema.apiName);
        this.loadSchemaData(schema.apiName);
    }
    
    /**
     * Check if selecting a schema would be out of sequential order
     * Returns true if this schema is not the first, not completed, and the previous schema is not completed
     */
    private isSchemaOutOfSequence(schemaIndex: number): boolean {
        // If invalid index, it's not out of sequence
        if (schemaIndex < 0 || schemaIndex >= this.processedSchemas.length) {
            return false;
        }
        
        const schema = this.processedSchemas[schemaIndex];
        
        // First schema is always allowed
        if (schemaIndex === 0) {
            return false;
        }
        
        // Already completed schemas are allowed
        if (schema.completed) {
            return false;
        }
        
        // Check if the previous enabled schema is completed
        for (let i = schemaIndex - 1; i >= 0; i--) {
            if (this.processedSchemas[i].enabled) {
                // If previous enabled schema is NOT completed, this is out of sequence
                if (!this.processedSchemas[i].completed) {
                    return true;
                }
                // Previous schema is completed, so this is in sequence
                return false;
            }
        }
        
        // No enabled schema before this one - shouldn't happen but allow it
        return false;
    }
    
    /**
     * Show warning confirmation dialog for out-of-sequence schema navigation
     */
    private showSequentialWarningDialog(): void {
        this.confirmationService.show({
            title: 'Attention Required',
            message: `You are about to prepare and execute the ${this.selectedSchema}. It is recommended that previous schemas be completed for sequential execution.`,
            warningText: 'Are you sure you want to proceed with selected schema?',
            confirmText: 'Proceed',
            cancelText: 'Cancel',
            type: 'warning',
            confirmCallback: () => this.proceedToReview(),
            cancelCallback: () => {} // Just close the dialog
        });
    }
    
    
    
    /**
     * Check if the currently selected schema is completed
     */
    public isSelectedSchemaCompleted(): boolean {
        const selectedSchemaObj = this.processedSchemas.find(s => s.displayName === this.selectedSchema);
        return selectedSchemaObj ? selectedSchemaObj.completed : false;
    }
    
    private loadSchemaData(schemaName: string): void {
        this.loadingSchemaData = true;
        // For RBU, pass normalize=true to get normalized data
        this.wellsApiService.getSchema(this.wellName, schemaName, true).subscribe({
            next: (data: any) => {
                this.schemaData = data;
                this.processSchemaData(data);
                this.loadingSchemaData = false;
            },
            error: (err: any) => {
                console.error('Error loading schema data:', err);
                this.notificationService.error('Failed to load schema data: ' + (err.error?.detail || err.message || 'Unknown error'));
                this.loadingSchemaData = false;
            }
        });
    }
    
    private processSchemaData(data: any): void {
        if (!data) {
            this.schemaDataSections = [];
            this.displayKeys = [];
            this.fieldTypeMap = {};
            return;
        }

        // Handle array of objects (sections)
        if (Array.isArray(data)) {
            this.schemaDataSections = data.map((section, sectionIndex) => {
                // Filter out keys ending with 'image_path', 'pdf_link', and unit variant keys
                const filteredKeys = Object.keys(section).filter(key => 
                    !key.toLowerCase().endsWith('image_path') && 
                    !key.toLowerCase().endsWith('pdf_link') &&
                    key.toLowerCase() !== 'converted_values' &&
                    !this.isUnitVariantKey(key)
                );

                // Create editable data for this section
                const sectionEditableData: { [key: string]: any } = {};
                filteredKeys.forEach(key => {
                    sectionEditableData[key] = section[key];
                    
                    // For ABU, detect and store the date format when loading
                    if (this.bu === 'ABU' && this.isDateField(key) && section[key]) {
                        this.dateFormatMap[key] = this.detectDateFormat(section[key]);
                        console.log(`[processSchemaData] Detected date format for "${key}": ${this.dateFormatMap[key]} (value: ${section[key]})`);
                    }
                    
                    // For ABU, detect and store the date format when loading
                    if (this.bu === 'ABU' && this.isDateField(key) && section[key]) {
                        this.dateFormatMap[key] = this.detectDateFormat(section[key]);
                        console.log(`[processSchemaData] Detected date format for "${key}": ${this.dateFormatMap[key]} (value: ${section[key]})`);
                    }
                });

                return {
                    index: sectionIndex,
                    originalData: { ...section },
                    editableData: sectionEditableData,
                    displayKeys: filteredKeys
                };
            });

            // Build field type map from first section to preserve data types
            if (this.schemaDataSections.length > 0) {
                const firstSection = this.schemaDataSections[0];
                const sourceData = firstSection.editableData || firstSection.originalData;
                
                this.fieldTypeMap = {};
                Object.keys(sourceData).forEach(key => {
                    const value = sourceData[key];
                    this.fieldTypeMap[key] = this.getDataType(value);
                });
            }

            // Collect ALL unique keys from ALL sections
            const allKeysSet = new Set<string>();
            this.schemaDataSections.forEach(section => {
                section.displayKeys.forEach((key: string) => allKeysSet.add(key));
            });
            
            // Convert to array (no special handling needed for display keys)
            this.displayKeys = Array.from(allKeysSet);
            
            // Initialize edit mode and change tracking arrays
            this.sectionEditMode = new Array(this.schemaDataSections.length).fill(false);
            this.sectionHasChanges = new Array(this.schemaDataSections.length).fill(false);
        } else {
            // Handle single object (legacy support)
            this.displayKeys = Object.keys(data).filter(key => 
                !key.toLowerCase().endsWith('image_path') && 
                !key.toLowerCase().endsWith('pdf_link') &&
                key.toLowerCase() !== 'converted_values' &&
                !this.isUnitVariantKey(key)
            );
            this.schemaDataSections = [];
            this.sectionEditMode = [];
            this.sectionHasChanges = [];
            this.fieldTypeMap = {};
        }
    }

    /**
     * Get the data type of a value
     * Distinguishes between int, float, and string based on actual JSON type
     * Do NOT parse strings to determine type - only check the JavaScript type
     */
    private getDataType(value: any): string {
        if (value === null || value === undefined) {
            return 'string';  // Default to string for null/undefined
        }
        
        // Check the actual JavaScript type from the API response
        if (typeof value === 'number') {
            // Only numbers are converted to int/float
            return Number.isInteger(value) ? 'int' : 'float';
        }
        
        // Everything else (including strings that look like numbers) is treated as string
        // e.g., "14.75" stays as string, "Actual" stays as string
        return 'string';
    }

    
    /**
     * Check if a key is a unit variant key or internal field that shouldn't be displayed
     * Excludes: converted_values, unit variant keys in unitVariantMap
     */
    private isUnitVariantKey(key: string): boolean {
        const lowerKey = key.toLowerCase();
        
        // Hide converted_values object itself
        if (lowerKey === 'converted_values') {
            return true;
        }
        
        // Check common unit variant patterns
        if (lowerKey.includes(' in ft') || 
            lowerKey.includes(' in m') || 
            lowerKey.includes(' in inches') || 
            lowerKey.includes(' in inch') || 
            lowerKey.includes('_in_ft') || 
            lowerKey.includes('_in_m') || 
            lowerKey.includes('_in_inches') ||
            lowerKey.includes('_in_inch')) {
            return true;
        }
        
        return false;
    }
    
    public loadWellFileAvailability(): void {
        this.loadingWellFiles = true;
        this.httpService.getWellFilesAvailability(this.wellName, this.bu).subscribe({
            next: (response: any) => {
                this.fileAvailability = response;
                console.log('[loadWellFileAvailability] File availability loaded:', this.fileAvailability);
                this.loadingWellFiles = false;
            },
            error: (error: any) => {
                console.error('[loadWellFileAvailability] Error loading file availability:', error);
                this.fileAvailability = null;
                this.loadingWellFiles = false;
            }
        });
    }

    /**
     * Get available document type options for the custom dropdown component
     * For ABU: returns DDR/DTR/WSS document types
     * For RBU: returns PDF file names from the availability response
     * Only returns actual available documents, not placeholder messages
     */
    public getDocumentTypeOptions(): string[] {
        const options: string[] = [];
        
        // For RBU, extract PDF file names from the files array
        if (this.bu === 'RBU' && this.fileAvailability?.files) {
            // Filter only PDF files
            const pdfFiles = this.fileAvailability.files.filter((file: any) => 
                file.extension === '.pdf' && file.file_name
            );
            return pdfFiles.map((file: any) => file.file_name);
        }
        
        // For ABU, return document type options
        if (this.fileAvailability?.availability?.ddr) {
            options.push(`Daily Drilling Report (${this.fileAvailability?.file_counts?.ddr || 0})`);
        }
        if (this.fileAvailability?.availability?.dtr) {
            options.push(`Daily Tour Report (${this.fileAvailability?.file_counts?.dtr || 0})`);
        }
        if (this.fileAvailability?.availability?.wss) {
            options.push(`Wells Service Sheet (${this.fileAvailability?.file_counts?.wss || 0})`);
        }
        
        return options;
    }

    public getSelectedDocLabel(): string {
        // For RBU, return the selected file name directly
        if (this.bu === 'RBU') {
            return this.selectedDocType || '';
        }
        
        // For ABU, map internal doc type codes to display labels
        const docTypeMap: { [key: string]: string } = {
            'ddr': `Daily Drilling Report (${this.fileAvailability?.file_counts?.ddr || 0})`,
            'dtr': `Daily Tour Report (${this.fileAvailability?.file_counts?.dtr || 0})`,
            'wss': `Wells Service Sheet (${this.fileAvailability?.file_counts?.wss || 0})`
        };
        
        return docTypeMap[this.selectedDocType] || '';
    }
    
    public onSupportedDocChange(selectedLabel: string): void {
        if (!selectedLabel || selectedLabel.toLocaleLowerCase().includes('no supported documents')) {
            this.selectedDocType = '';
            this.selectedImageUrl = '';
            this.previewError = '';
            return;
        }

        // For RBU, the selected label is the PDF file name
        if (this.bu === 'RBU') {
            this.selectedDocType = selectedLabel;  // Store the file name
            this.loadSelectedDocument();
            return;
        }
        
        // For ABU, extract the doc type from the selected label using case-insensitive matching
        const labelLower = selectedLabel.toLocaleLowerCase();
        this.selectedDocType = '';
        
        if (labelLower.includes('daily drilling report')) {
            this.selectedDocType = 'ddr';
        } else if (labelLower.includes('daily tour report')) {
            this.selectedDocType = 'dtr';
        } else if (labelLower.includes('wells service sheet')) {
            this.selectedDocType = 'wss';
        }

        if (!this.selectedDocType) {
            this.selectedImageUrl = '';
            this.previewError = 'Unsupported document selection';
            return;
        }
        
        // Load the selected document
        this.loadSelectedDocument();
    }
    
    public loadSelectedDocument(): void {
        if (!this.selectedDocType) {
            this.selectedImageUrl = '';
            return;
        }
        
        this.loadingImage = true;
        
        // For RBU, use the file name to find file details and call with file path
        if (this.bu === 'RBU') {
            // Find the file object from availability response
            const fileObj = this.fileAvailability?.files?.find((f: any) => f.file_name === this.selectedDocType);
            
            if (!fileObj) {
                this.selectedImageUrl = '';
                this.previewError = 'File not found';
                this.loadingImage = false;
                return;
            }
            
            // Call API with file_name and file_path for RBU
            this.httpService.getWellFiles(this.wellName, undefined, fileObj.file_name, fileObj.file_path, this.bu).subscribe({
                next: (response: any) => {
                    let sasUrl: string | null = null;
                    let fileName: string | null = null;

                    if (response && response.files) {
                        if (Array.isArray(response.files) && response.files.length > 0) {
                            // Array response: { files: [ { sas_url, file_name }, ... ] }
                            sasUrl = response.files[0].sas_url;
                            fileName = response.files[0].file_name;
                        } else if (typeof response.files === 'object' && !Array.isArray(response.files) && response.files.sas_url) {
                            // Object response: { files: { sas_url, file_name } }
                            sasUrl = response.files.sas_url;
                            fileName = response.files.file_name;
                        }
                    }

                    if (sasUrl) {
                        this.selectedImageUrl = sasUrl;
                        this.previewDocumentType = 'pdf';
                        this.previewDocumentTitle = fileName || fileObj.file_name;
                        this.loadingImage = false;
                    } else {
                        this.selectedImageUrl = '';
                        this.previewError = 'No SAS URL found';
                        this.loadingImage = false;
                    }
                },
                error: (error: any) => {
                    console.error('Error loading document SAS URL:', error);
                    this.previewError = 'Failed to load document';
                    this.loadingImage = false;
                }
            });
            return;
        }
        
        // For ABU, use the file type (existing logic)
        this.httpService.getWellFiles(this.wellName, this.selectedDocType, undefined, undefined, this.bu).subscribe({
            next: (response: any) => {
                let sasUrl: string | null = null;
                let fileName: string | null = null;

                if (response && response.files) {
                    if (Array.isArray(response.files) && response.files.length > 0) {
                        // Array response: { files: [ { sas_url, file_name }, ... ] }
                        sasUrl = response.files[0].sas_url;
                        fileName = response.files[0].file_name;
                    } else if (typeof response.files === 'object' && !Array.isArray(response.files) && response.files.sas_url) {
                        // Object response: { files: { sas_url, file_name } }
                        sasUrl = response.files.sas_url;
                        fileName = response.files.file_name;
                    }
                }

                if (sasUrl) {
                    this.selectedImageUrl = sasUrl;
                    this.previewDocumentType = 'pdf';
                    this.previewDocumentTitle = fileName || `${this.selectedDocType} - ${this.wellName}`;
                    this.loadingImage = false;
                } else {
                    this.selectedImageUrl = '';
                    this.previewError = 'No document found';
                    this.loadingImage = false;
                }
            },
            error: (error: any) => {
                console.error('Error loading document:', error);
                this.previewError = 'Failed to load document';
                this.loadingImage = false;
            }
        });
    }
    
    public closeDocumentPreview(): void {
        this.selectedImageUrl = '';
        this.previewError = '';
        this.selectedDocType = '';
    }
    
    public clearDocTypeSelection(event: Event): void {
        event.stopPropagation();
        this.selectedDocType = '';
    }
    
    public navigateBackToList(): void {
        this.router.navigate(['/wells']);
    }
    
    public onNext(): void {
        // Check if trying to navigate to a schema that's out of sequence
        const currentSchemaIndex = this.processedSchemas.findIndex(s => s.displayName === this.selectedSchema);
        const isOutOfSequence = this.isSchemaOutOfSequence(currentSchemaIndex);
        
        // Get field validation warning message
        const fieldValidationMessage = this.getFieldValidationMessage(this.selectedSchema);
        
        // If BOTH out of sequence AND has field validation requirement, merge into one dialog
        if (isOutOfSequence && fieldValidationMessage) {
            const mergedMessage = `It is recommended that previous schemas be completed for sequential execution. Additionally, ${fieldValidationMessage}`;
            
            this.confirmationService.show({
                title: 'Attention Required',
                message: mergedMessage,
                warningText: 'Are you sure you want to proceed?',
                confirmText: 'Proceed',
                cancelText: 'Cancel',
                type: 'warning',
                confirmCallback: () => this.proceedToReview(),
                cancelCallback: () => {} // Just close the dialog
            });
            return;
        }
        
        // If only out of sequence (no field validation)
        if (isOutOfSequence) {
            this.showSequentialWarningDialog();
            return;
        }
        
        // If only field validation (no sequence issue), show field validation warning
        if (fieldValidationMessage) {
            this.confirmationService.show({
                type: 'warning',
                title: 'Attention Required',
                message: fieldValidationMessage,
                confirmText: 'Proceed',
                cancelText: 'Cancel',
                confirmButtonType: 'save',
                confirmCallback: () => this.proceedToReview(),
                cancelCallback: () => {} // Do nothing on cancel
            });
            return;
        }
        
        // Proceed with navigation to review page
        this.proceedToReview();
    }
        
    /**
     * Proceed to review page after validation or confirmation
     */
    private proceedToReview(): void {
        // Store current selected schema in tab navigation service to persist across navigation
        // This ensures both display name and API name are properly tracked
        this.tabNavigationService.setSelectedSchema(this.selectedSchema, this.selectedSchemaApiName);
        this.router.navigate(['/wells', this.wellName, 'review']);
    }
    
    public onReviewBack(): void {
        // Clear selected schema first
        this.tabNavigationService.clearSelectedSchema();
        this.selectedSchemaApiName = '';
        this.displayKeys = [];
        
        // Mark valid navigation flow before navigating to prevent refresh detection
        // stagesCompleted is already persisted in the service from before we navigated to review
        // No need to fetch from API again
        this.tabNavigationService.setValidNavigationFlow(true);
        
        // Navigate back
        this.router.navigate(['/wells', this.wellName]);
        
        // Reload schemas to get fresh data
        setTimeout(() => {
            this.loadSchemas();
            this.loadWellFileAvailability();
        }, 100);
    }
    
    public onReviewSubmit(): void {
        this.router.navigate(['/wells', this.wellName, 'review', 'approve']);
    }
    
    public getActiveSchemaCount(): number {
        return this.processedSchemas.filter(s => s.enabled).length;
    }
    
    /**
     * Check if a field should be rendered as a dropdown
     * Based on schema name and field name mapping
     */
    public isDropdownField(key: string): boolean {
        const schemaName = this.selectedSchema;
        if (!schemaName) return false;
        
        const schemaOptions = this.DROPDOWN_OPTIONS[schemaName];
        if (!schemaOptions) return false;
        
        return key in schemaOptions;
    }
    
    /**
     * Get dropdown options for a specific field
     * Returns predefined options for the schema/field
     */
    public getDropdownOptions(key: string): string[] {
        const schemaName = this.selectedSchema;
        if (!schemaName) return [];
        
        const schemaOptions = this.DROPDOWN_OPTIONS[schemaName];
        // console.log("schemaOptions:", schemaOptions);
        if (!schemaOptions) return [];
        
        return schemaOptions[key] || [];
    }

    /**
     * Normalize a string for comparison (lowercase and trim)
     * Used to match dropdown values regardless of case
     */
    public normalizeForComparison(value: any): string {
        if (!value) return '';
        return String(value).toLowerCase().trim();
    }

    /**
     * Find the exact dropdown option that matches a value (case-insensitive)
     * Returns the actual option string or the original value
     */
    public getMatchingDropdownOption(key: string, value: any): string {
        if (!value) return '';
        
        const normalizedValue = this.normalizeForComparison(value);
        const options = this.getDropdownOptions(key);
        console.log("Dropdown options for key", key, ":", options);
        
        // Find the option that matches (case-insensitive)
        const matchingOption = options.find(opt => this.normalizeForComparison(opt) === normalizedValue);
        console.log("matchinOption", matchingOption)
        return matchingOption || value;
    }
    
    /**
     * Get the value to display in an input field during edit mode
     * Strips the '-' display symbol and null/undefined values so the input shows empty
     * This prevents the display symbol from appearing in editable fields
     */
    public getEditInputValue(value: any): any {
        if (value === '-' || value === null || value === undefined) {
            return '';
        }
        return value;
    }

    /**
     * Get the value to display in cells/tooltips (read-only mode)
     * For date fields, converts YYYY-MM-DD to DD-MM-YYYY format
     * Returns '-' for empty/null values
     */
    public getDisplayValue(key: string, value: any): string {
        // Handle empty/null values
        if (value === null || value === undefined || value === '') {
            return '-';
        }

        // If it's a date field, format it for display
        if (this.isDateField(key) && typeof value === 'string') {
            return this.formatDateForDisplay(value);
        }

        // For non-date fields, return as string
        return String(value);
    }
    
    public getDisplayLabel(key: string): string {
        if (!key) return '';
        
        // Get WellView schema key for unit lookup
        const wellViewSchemaKey = this.wellViewMappingService.getWellViewSchemaName(this.selectedSchemaApiName);
        
        // FIRST PRIORITY: Use WellViewMappingService to get the label and unit from mapping.json
        // This gives us the single authoritative unit (e.g., "feet", "inch", "lb/ft")
        if (wellViewSchemaKey) {
            const label = this.wellViewMappingService.getFieldLabelSync(wellViewSchemaKey, key);
            const unit = this.wellViewMappingService.getFieldUnitSync(wellViewSchemaKey, key);
            
            // If we have both label and unit from mapping, use them
            if (label && unit) {
                return `${label} (${unit})`;
            }
            
            // If we only have label, return it
            if (label) {
                return label;
            }
        }
        
        // SECOND: If the key contains spaces, slashes, or is already a label format
        // This handles cases where the API returns formatted labels like "API Number", "Top Depth", etc.
        if (/[\s\/\-]/.test(key)) {
            // Try to get unit from mapping.json by matching the label
            if (wellViewSchemaKey) {
                const unit = this.wellViewMappingService.getFieldUnitSync(wellViewSchemaKey, key);
                if (unit) {
                    return `${key} (${unit})`;
                }
            }
            return key;
        }
        
        // THIRD: Check if this field has unit variants in converted_values (dynamic detection)
        // This is a fallback for fields not in mapping.json
        if (this.schemaDataSections && this.schemaDataSections.length > 0) {
            for (const section of this.schemaDataSections) {
                const dataRecord = section.originalData || section;
                if (this.unitConversionService.hasUnitVariants(key, dataRecord)) {
                    const availableUnits = this.unitConversionService.getAvailableUnits(key, dataRecord);
                    if (availableUnits.length > 0) {
                        // Format units with display labels
                        const unitLabels = availableUnits.map(unit => 
                            this.unitConversionService.getUnitDisplayLabel(unit)
                        ).join('/');
                        
                        // Format the key as a label
                        const fieldLabel = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                        return `${fieldLabel} (${unitLabels})`;
                    }
                }
            }
        }
        
        // Fallback: format the key
        return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
    
    /**
     * Check if a field is read-only (unique identifier that cannot be changed)
     * Returns true for fields like API Number, WellName, idwell, etc.
     */
    public isFieldReadOnly(key: string): boolean {
        if (!key) return false;
        
        // List of unique identifier field names (case-insensitive)
        // These fields cannot be edited as they are unique identifiers
        const readOnlyFields = [
            'api number',
            'api_number',
            'apinumber',
            'wellname',
            'well name',
            'well_name',
            'idwell',
            'id_well'
        ];
        
        const normalizedKey = key.toLowerCase().replace(/[\s_]/g, '');
        return readOnlyFields.some(field => field.replace(/[\s_]/g, '') === normalizedKey);
    }

    /**
     * Check if a field is a date field based on naming conventions
     * Returns true for fields with 'date', 'time', 'day', 'month', 'year', etc.
     */
    public isDateField(key: string): boolean {
        if (!key) return false;

        const datePatterns = [
            'date',
            'time',
            'day',
            'month',
            'year',
            'datetime',
            'date_time',
            'spud',
            'start_date',
            'end_date',
            'spud_date',
            'run_date',
            'start_date',
            'end_date',
            'spud_date',
            'run_date',
            'completion',
            'pump_start_date',
            'pump_end_date',
            'pump_start_date',
            'pump_end_date',
            'production',
            'plugged',
            'abandoned',
            'installed',
            'cemented',
            'perforated',
            'tested',
            'drilled'
        ];
        // console.log('Date fields detected:', this.displayKeys.filter(k => this.isDateField(k)));
        // console.log('Date fields detected:', this.displayKeys.filter(k => this.isDateField(k)));

        const normalizedKey = key.toLowerCase().replace(/[\s_]/g, '');
        return datePatterns.some(pattern => normalizedKey.includes(pattern.replace(/[\s_]/g, '')));
    }

    /**
     * Format Date object to string format: "YYYY-MM-DD HH:mm:ss"
     */
    public formatDateTimeForStorage(date: Date | null): string {
        if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
            return '';
        }

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');

        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }

    /**
     * Format date string for display in UI (cells, tooltips, etc.)
     * Converts YYYY-MM-DD to DD-MM-YYYY for consistent display
     * Handles both formats and datetime strings
     */
    public formatDateForDisplay(dateString: string | null): string {
        if (!dateString || typeof dateString !== 'string') {
            return '-';
        }

        try {
            // Extract date part only (remove time if present)
            let dateOnly = dateString;
            if (dateString.includes(' ')) {
                dateOnly = dateString.split(' ')[0];
            }

            // If already in DD-MM-YYYY format, return as is
            if (/^\d{2}-\d{2}-\d{4}$/.test(dateOnly)) {
                return dateOnly;
            }

            // Convert YYYY-MM-DD to DD-MM-YYYY
            const isoMatch = dateOnly.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            if (isoMatch) {
                const [, year, month, day] = isoMatch;
                return `${day}-${month}-${year}`;
            }

            // If slash-separated YYYY/MM/DD, convert to DD-MM-YYYY
            const isoSlashMatch = dateOnly.match(/^(\d{4})\/(\d{2})\/(\d{2})$/);
            if (isoSlashMatch) {
                const [, year, month, day] = isoSlashMatch;
                return `${day}-${month}-${year}`;
            }

            // Already in other formats, return as is
            return dateOnly;
        } catch (error) {
            console.error('Error formatting date for display:', error);
            return dateString || '-';
        }
    }

    /**
     * Parse a date string to Date object
     * Handles multiple formats: YYYY-MM-DD, DD-MM-YYYY, YYYY-MM-DD HH:mm:ss, DD-MM-YYYY HH:mm:ss
     */
    public parseDateTimeString(dateString: string | null): Date | null {
        if (!dateString || typeof dateString !== 'string') {
            return null;
        }

        try {
            // Format 1: YYYY-MM-DD HH:mm:ss (ISO format with time)
            let dateRegex = /^(\d{4})-(\d{2})-(\d{2})(?:\s+(\d{2}):(\d{2}):(\d{2}))?$/;
            let match = dateString.match(dateRegex);

            if (match) {
                const year = parseInt(match[1], 10);
                const month = parseInt(match[2], 10) - 1; // Month is 0-indexed in JS
                const day = parseInt(match[3], 10);
                const hours = match[4] ? parseInt(match[4], 10) : 0;
                const minutes = match[5] ? parseInt(match[5], 10) : 0;
                const seconds = match[6] ? parseInt(match[6], 10) : 0;

                const date = new Date(year, month, day, hours, minutes, seconds);
                if (!isNaN(date.getTime())) {
                    return date;
                }
            }

            // Format 2: DD-MM-YYYY HH:mm:ss or DD-MM-YYYY
            dateRegex = /^(\d{2})-(\d{2})-(\d{4})(?:\s+(\d{2}):(\d{2}):(\d{2}))?$/;
            match = dateString.match(dateRegex);

            if (match) {
                const day = parseInt(match[1], 10);
                const month = parseInt(match[2], 10) - 1; // Month is 0-indexed in JS
                const year = parseInt(match[3], 10);
                const hours = match[4] ? parseInt(match[4], 10) : 0;
                const minutes = match[5] ? parseInt(match[5], 10) : 0;
                const seconds = match[6] ? parseInt(match[6], 10) : 0;

                // Validate month and day
                if (month >= 0 && month <= 11 && day >= 1 && day <= 31) {
                    const date = new Date(year, month, day, hours, minutes, seconds);
                    if (!isNaN(date.getTime())) {
                        return date;
                    }
                }
            }

            return null;
        } catch (error) {
            console.error('Error parsing date string:', error);
            return null;
        }
    }

    /**
     * Format datetime string for HTML5 datetime-local input (YYYY-MM-DDTHH:mm:ss)
     * RBU ONLY - handles datetime with time component
     * Supports both YYYY-MM-DD and DD-MM-YYYY formats
     */
    public formatForDateTimeInput(dateTimeString: string | null): string {
        if (!dateTimeString || typeof dateTimeString !== 'string') {
            return '';
        }
        
        try {
            // Try Format 1: YYYY-MM-DD HH:mm:ss → YYYY-MM-DDTHH:mm:ss
            let match = dateTimeString.match(/^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2}:\d{2})$/);
            if (match) {
                return `${match[1]}T${match[2]}`;
            }

            // Try Format 2: DD-MM-YYYY HH:mm:ss → Convert to YYYY-MM-DDTHH:mm:ss
            match = dateTimeString.match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}:\d{2}:\d{2})$/);
            if (match) {
                const day = match[1];
                const month = match[2];
                const year = match[3];
                const time = match[4];
                return `${year}-${month}-${day}T${time}`;
            }

            // Try Format 3: YYYY-MM-DD (no time) → add T00:00:00
            match = dateTimeString.match(/^(\d{4}-\d{2}-\d{2})$/);
            if (match) {
                return `${match[1]}T00:00:00`;
            }

            // Try Format 4: DD-MM-YYYY (no time) → convert and add T00:00:00
            match = dateTimeString.match(/^(\d{2})-(\d{2})-(\d{4})$/);
            if (match) {
                const day = match[1];
                const month = match[2];
                const year = match[3];
                return `${year}-${month}-${day}T00:00:00`;
            }
            
            // If already in correct format or other format, try replacing space with T
            return dateTimeString.replace(' ', 'T');
        } catch (error) {
            console.error('Error formatting datetime for input:', error);
            return '';
        }
    }


    /**
     * Format date string for HTML5 date input (YYYY-MM-DD)
     * ABU ONLY - handles date-only values, preserves original format
     * Supports multiple formats: YYYY-MM-DD, DD-MM-YYYY, YYYY-MM-DD HH:mm:ss, DD-MM-YYYY HH:mm:ss
     * Supports 2-digit and 4-digit year formats
     */
    public formatForDateInput(dateString: string | null): string {
        if (!dateString || typeof dateString !== 'string') {
            return '';
        }
        
        try {
            // First, strip off any time component if present
            let dateOnly = dateString;
            if (dateString.includes(' ')) {
                dateOnly = dateString.split(' ')[0];
            }

            // If already in YYYY-MM-DD format, use as is
            if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
                return dateOnly;
            }
            
            // For dash-separated dates, need to distinguish DD-MM-YYYY from MM-DD-YYYY
            const dashMatch = dateOnly.match(/^(\d{2})-(\d{2})-(\d{4})$/);
            if (dashMatch) {
                const [, first, second, year] = dashMatch;
                const firstNum = parseInt(first);
                const secondNum = parseInt(second);
                
                // Smart detection:
                // If first number > 12, it MUST be day (DD-MM-YYYY)
                // If second number > 12, it MUST be day (MM-DD-YYYY)
                // Otherwise assume MM-DD-YYYY (common US format)
                
                if (firstNum > 12 && secondNum <= 12) {
                    // DD-MM-YYYY: "16-05-1968" or "10-11-2000" → "1968-05-16" or "2000-11-10"
                    return `${year}-${second}-${first}`;
                } else {
                    // MM-DD-YYYY: "05-16-1968" → "1968-05-16"
                    // Or ambiguous like "05-06-1968", default to MM-DD-YYYY
                    return `${year}-${first}-${second}`;
                }
            }
            
            // If slash-separated, try common formats (supports both 2-digit and 4-digit years)
            const slashMatch = dateOnly.match(/^(\d{1,4})[\/](\d{1,2})[\/](\d{1,4})$/);
            if (slashMatch) {
                const [, first, second, third] = slashMatch;
                const firstNum = parseInt(first);
                const secondNum = parseInt(second);
                const thirdNum = parseInt(third);
                
                // Check if third part is a year (either 2-digit or 4-digit)
                const isThirdAYear = thirdNum > 31 || (thirdNum >= 0 && thirdNum <= 99); // 2-digit year
                
                if (firstNum > 31) {
                    // YYYY/MM/DD format (4-digit year first)
                    return `${first}-${second.padStart(2, '0')}-${third.padStart(2, '0')}`;
                } else if (isThirdAYear) {
                    // MM/DD/YYYY(YY) or DD/MM/YYYY(YY) format - thirdNum is year
                    let year = third;
                    
                    // Convert 2-digit year to 4-digit year
                    if (year.length === 2) {
                        const yearNum = parseInt(year);
                        // Assume 00-30 = 2000-2030, 31-99 = 1931-1999
                        if (yearNum <= 30) {
                            year = `20${year}`;
                        } else {
                            year = `19${year}`;
                        }
                    }
                    
                    if (firstNum > 12 && secondNum <= 12) {
                        // DD/MM/YYYY(YY): "20/11/98" → "1998-11-20"
                        return `${year}-${second.padStart(2, '0')}-${first.padStart(2, '0')}`;
                    } else {
                        // MM/DD/YYYY(YY): "05/16/1968" → "1968-05-16"
                        return `${year}-${first.padStart(2, '0')}-${second.padStart(2, '0')}`;
                    }
                }
            }
            
            // Fallback: return as is and let date input handle it
            return dateOnly;
        } catch (error) {
            console.error('Error formatting date for input:', error);
            return dateString || '';
        }
    }

    /**
     * Store the original date format for a field (for preserving format on save)
     * Called when loading schema data
     */
    private dateFormatMap: { [key: string]: string } = {}; // Maps field key to original format

    /**
     * Detect and store the format of an ABU date string
     * Uses smart detection: if first > 12, it's DD-MM-YYYY; if second > 12, it's MM-DD-YYYY
     * Supports both 4-digit and 2-digit year formats
     * Also handles datetime strings by stripping time component first
     */
    private detectDateFormat(dateString: string | null): string {
        if (!dateString || typeof dateString !== 'string') {
            return 'yyyy-mm-dd'; // Default
        }

        // Strip time component if present
        let dateOnly = dateString;
        if (dateString.includes(' ')) {
            dateOnly = dateString.split(' ')[0];
        }

        // Check format patterns
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
            return 'yyyy-mm-dd';
        }
        
        // For dash-separated formats with 4-digit year, use smart detection
        const dashMatch = dateOnly.match(/^(\d{2})-(\d{2})-(\d{4})$/);
        if (dashMatch) {
            const [, first, second] = dashMatch;
            const firstNum = parseInt(first);
            const secondNum = parseInt(second);
            
            // If first > 12, must be DD-MM-YYYY (e.g., "16-05-1968" or "10-11-2000")
            if (firstNum > 12) {
                return 'dd-mm-yyyy';
            }
            // If second > 12, must be MM-DD-YYYY (e.g., "05-16-1968")  
            if (secondNum > 12) {
                return 'mm-dd-yyyy';
            }
            // Ambiguous case like "05-06-1968", default to MM-DD-YYYY
            return 'mm-dd-yyyy';
        }
        
        // For slash-separated formats with 4-digit year
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateOnly)) {
            const slashMatch4 = dateOnly.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
            if (slashMatch4) {
                const [, first, second] = slashMatch4;
                const firstNum = parseInt(first);
                const secondNum = parseInt(second);
                
                // Smart detection for slash format
                if (firstNum > 12) {
                    return 'dd/mm/yyyy';
                }
                if (secondNum > 12) {
                    return 'mm/dd/yyyy';
                }
                return 'mm/dd/yyyy'; // Default to MM/DD/YYYY
            }
        }
        
        // For slash-separated formats with 2-digit year (e.g., "20/11/98")
        if (/^\d{2}\/\d{2}\/\d{2}$/.test(dateOnly)) {
            const slashMatch2 = dateOnly.match(/^(\d{2})\/(\d{2})\/(\d{2})$/);
            if (slashMatch2) {
                const [, first, second] = slashMatch2;
                const firstNum = parseInt(first);
                const secondNum = parseInt(second);
                
                // Smart detection for 2-digit year
                if (firstNum > 12) {
                    return 'dd/mm/yy';
                }
                if (secondNum > 12) {
                    return 'mm/dd/yy';
                }
                return 'mm/dd/yy'; // Default to MM/DD/YY
            }
        }
        
        if (/^\d{4}\/\d{2}\/\d{2}$/.test(dateOnly)) return 'yyyy/mm/dd';

        return 'yyyy-mm-dd'; // Default fallback
    }

    /**
     * Convert normalized date (YYYY-MM-DD) back to original format for ABU
     * Supports both 2-digit and 4-digit year formats
     */
    public formatDateToOriginalFormat(normalizedDate: string, originalFormat: string): string {
        if (!normalizedDate) return '';

        try {
            // Parse the normalized YYYY-MM-DD format
            const match = normalizedDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            if (!match) return normalizedDate;

            const [, year, month, day] = match;
            
            // Convert 4-digit year to 2-digit year if needed
            let year2Digit = year.substring(2); // Get last 2 digits

            // Convert back to original format (case-insensitive comparison)
            const format = originalFormat.toLowerCase();
            
            switch (format) {
                case 'mm-dd-yyyy':
                    return `${month}-${day}-${year}`;
                case 'dd-mm-yyyy':
                    return `${day}-${month}-${year}`;
                case 'mm/dd/yyyy':
                    return `${month}/${day}/${year}`;
                case 'dd/mm/yyyy':
                    return `${day}/${month}/${year}`;
                case 'yyyy/mm/dd':
                    return `${year}/${month}/${day}`;
                case 'mm/dd/yy':
                    return `${month}/${day}/${year2Digit}`;
                case 'dd/mm/yy':
                    return `${day}/${month}/${year2Digit}`;
                case 'mm-dd-yy':
                    return `${month}-${day}-${year2Digit}`;
                case 'dd-mm-yy':
                    return `${day}-${month}-${year2Digit}`;
                case 'yyyy-mm-dd':
                default:
                    return normalizedDate;
            }
        } catch (error) {
            console.error('Error formatting date to original format:', error);
            return normalizedDate;
        }
    }
    
    /**
     * Handle datetime-local input change (RBU ONLY)
     */
    public onDateTimeInputChange(sectionIndex: number, key: string, value: string): void {
        const section = this.schemaDataSections[sectionIndex];
        if (!section) {
            return;
        }
        
        if (!value) {
            this.updateSectionField(sectionIndex, key, '');
            return;
        }
        
        try {
            // Convert from "YYYY-MM-DDTHH:mm:ss" to "YYYY-MM-DD HH:mm:ss"
            const formattedDateTime = value.replace('T', ' ');
            this.updateSectionField(sectionIndex, key, formattedDateTime);
        } catch (error) {
            console.error('Error processing datetime input:', error);
        }
    }

    /**
     * Handle date input change (ABU ONLY - date only, no time)
     * Preserves the original format detected from the API
     */
    public onDateInputChange(sectionIndex: number, key: string, value: string): void {
        const section = this.schemaDataSections[sectionIndex];
        if (!section) {
            return;
        }
        
        if (!value) {
            this.updateSectionField(sectionIndex, key, '');
            return;
        }
        
        try {
            // value comes in as YYYY-MM-DD from date input
            // Convert it back to the original format for storage
            const originalFormat = this.dateFormatMap[key] || 'yyyy-mm-dd';
            const formattedDate = this.formatDateToOriginalFormat(value, originalFormat);
            console.log(`[onDateInputChange] Key: ${key}, Value: ${value}, Format: ${originalFormat}, Formatted: ${formattedDate}`);
            this.updateSectionField(sectionIndex, key, formattedDate);
        } catch (error) {
            console.error('Error processing date input:', error);
        }
    }
    
    /**
     * Get the date part from a datetime string (YYYY-MM-DD)
     */
    public getDatePart(dateTimeString: string | null): string {
        if (!dateTimeString || typeof dateTimeString !== 'string') {
            return '-';
        }
        
        // Extract date part (YYYY-MM-DD)
        const dateMatch = dateTimeString.match(/^(\d{4}-\d{2}-\d{2})/);
        return dateMatch ? dateMatch[1] : dateTimeString.split(' ')[0] || '-';
    }
    
    /**
     * Get the time part from a datetime string (HH:mm:ss)
     */
    public getTimePart(dateTimeString: string | null): string {
        if (!dateTimeString || typeof dateTimeString !== 'string') {
            return '00:00:00';
        }
        
        // Extract time part (HH:mm:ss)
        const timeMatch = dateTimeString.match(/(\d{2}:\d{2}:\d{2})$/);
        if (timeMatch) {
            return timeMatch[1];
        }
        
        // Try to extract time from space-separated format
        const parts = dateTimeString.split(' ');
        if (parts.length >= 2) {
            return parts[1] || '00:00:00';
        }
        
        return '00:00:00';
    }
    
    /**
     * Check if edit button should be disabled for this section
     * Returns true if another record is being edited
     */
    public isEditDisabledForSection(sectionIndex: number): boolean {
        // Check if any OTHER record is being edited
        return this.sectionEditMode.some((isEditing, index) => isEditing && index !== sectionIndex);
    }

    /**
     * Check if delete button should be disabled for this section
     * Returns true if any record is being edited
     */
    public isDeleteDisabledForSection(sectionIndex: number): boolean {
        // Disable delete if any record is being edited
        return this.sectionEditMode.some(isEditing => isEditing);
    }
    
    public toggleEditMode(index: number): void {
        // Prevent toggling edit if another record is being edited
        if (this.isEditDisabledForSection(index)) {
            this.notificationService.info('Please finish editing the current record first');
            return;
        }
        
        // If currently in edit mode and trying to close
        if (this.sectionEditMode[index] && this.sectionHasChanges[index]) {
            // Show confirmation dialog if there are unsaved changes
            this.sectionIndexToCancel = index;
            this.confirmationService.show({
                title: 'Discard Changes?',
                message: 'You have unsaved changes. Are you sure you want to close without saving?',
                warningText: 'All changes will be lost.',
                confirmText: 'Yes',
                cancelText: 'No',
                type: 'info',
                confirmButtonType: 'delete',
                confirmCallback: () => {
                    this.confirmEditCancel();
                },
                cancelCallback: () => {
                    this.sectionIndexToCancel = -1;
                }
            });
            this.confirmationService.show({
                title: 'Discard Changes?',
                message: 'You have unsaved changes. Are you sure you want to close without saving?',
                warningText: 'All changes will be lost.',
                confirmText: 'Yes',
                cancelText: 'No',
                type: 'info',
                confirmButtonType: 'delete',
                confirmCallback: () => {
                    this.confirmEditCancel();
                },
                cancelCallback: () => {
                    this.sectionIndexToCancel = -1;
                }
            });
        } else {
            // No changes, just toggle edit mode
            this.sectionEditMode[index] = !this.sectionEditMode[index];
            // Update navigation guard flag
            this.updateNavigationGuard();
            // Update navigation guard flag
            this.updateNavigationGuard();
        }
    }
    
    public deleteRecord(index: number): void {
        // Prevent deletion if any record is being edited
        if (this.isDeleteDisabledForSection(index)) {
            this.notificationService.info('Please finish editing the current record first');
            return;
        }
        
        // Store the index of the record to be deleted
        this.recordToDelete = index;
        // Show confirmation dialog using ConfirmationService
        this.confirmationService.show({
            title: 'Confirm Deletion',
            message: 'Are you sure you want to delete this record?',
            warningText: 'This action cannot be reversed.',
            confirmText: 'Delete',
            cancelText: 'Cancel',
            type: 'info',
            confirmButtonType: 'delete',
            confirmCallback: () => {
                this.performDelete();
            },
            cancelCallback: () => {
                this.recordToDelete = -1;
            }
        });
        // Update navigation guard flag
        this.updateNavigationGuard();
        // Show confirmation dialog using ConfirmationService
        this.confirmationService.show({
            title: 'Confirm Deletion',
            message: 'Are you sure you want to delete this record?',
            warningText: 'This action cannot be reversed.',
            confirmText: 'Delete',
            cancelText: 'Cancel',
            type: 'info',
            confirmButtonType: 'delete',
            confirmCallback: () => {
                this.performDelete();
            },
            cancelCallback: () => {
                this.recordToDelete = -1;
            }
        });
        // Update navigation guard flag
        this.updateNavigationGuard();
    }
    
    public saveAllChanges(): void {
        // Save directly without confirmation dialog
        this.performSave();
        // Update navigation guard flag after save
        this.updateNavigationGuard();
        // Update navigation guard flag after save
        this.updateNavigationGuard();
    }
    
    private performSave(): void {
        this.isSaving = true;
        
        // Get user ID from settings
        this.appSettingsService.getSettings().then(settings => {
            const userId = settings?.user?.username || settings?.user?.email || 'unknown';
            
            // Collect all changed sections and prepare the full record data for API
            const changedSections = this.schemaDataSections
                .map((section, index) => ({
                    section,
                    index,
                    hasChanges: this.sectionHasChanges[index]
                }))
                .filter(item => item.hasChanges);
            
            console.log('Saving changes for sections:', changedSections);
            
            if (changedSections.length === 0) {
                this.isSaving = false;
                this.notificationService.info('No changes to save');
                return;
            }
            
            // Prepare the complete records array with all current data
            // Convert empty strings to null for all fields
            const updatedRecords = this.schemaDataSections.map(section => 
                this.cleanupRecordData(section.editableData)
            );
            
            console.log('Saving updated records:', updatedRecords);
            
            // Call API to save suggestions (this automatically handles logs creation)
            this.httpService.saveEngineerSuggestions(
                this.wellName,
                this.selectedSchemaApiName,
                userId,
                updatedRecords
            ).subscribe({
                next: (response: any) => {
                    console.log('Suggestions saved successfully:', response);
                    
                    this.isSaving = false;
                    
                    // Reset all change tracking
                    this.sectionHasChanges = new Array(this.schemaDataSections.length).fill(false);
                    
                    // Close all edit modes
                    this.sectionEditMode = new Array(this.schemaDataSections.length).fill(false);
                    
                    // Update original data to match current editable data
                    // Preserve image path fields and other metadata
                    this.schemaDataSections.forEach(section => {
                        const preservedImagePaths: any = {};
                        
                        // Preserve all *_image_path fields from originalData
                        if (section.originalData) {
                            Object.keys(section.originalData).forEach(key => {
                                if (key.endsWith('_image_path')) {
                                    preservedImagePaths[key] = section.originalData[key];
                                }
                            });
                        }
                        
                        // Merge: editableData + preserved image paths
                        section.originalData = { ...section.editableData, ...preservedImagePaths };
                        
                        // Re-detect date formats for all date fields in the updated data
                        // This ensures the dateFormatMap is correct after save
                        if (section.originalData) {
                            Object.keys(section.originalData).forEach(key => {
                                if (this.isDateField(key)) {
                                    const dateValue = section.originalData[key];
                                    if (dateValue) {
                                        this.dateFormatMap[key] = this.detectDateFormat(dateValue);
                                        console.log(`[Save] Detected date format for ${key}:`, this.dateFormatMap[key], 'value:', dateValue);
                                    }
                                }
                            });
                        }
                    });
                    
                    // Update navigation guard flag
                    this.updateNavigationGuard();
                    
                    this.notificationService.success('Changes saved successfully');
                },
                error: (err: any) => {
                    console.error('Error saving suggestions:', err);
                    this.isSaving = false;
                    this.notificationService.error('Failed to save changes: ' + (err.error?.detail || err.message || 'Unknown error'));
                }
            });
        }).catch(err => {
            console.error('Error getting user settings:', err);
            this.isSaving = false;
            this.notificationService.error('Failed to get user settings');
        });
    }

    /**
     * Clean up record data by sending all fields with actual values or null for empty fields
     * Ensures all keys are included in the payload for proper data synchronization
     * Preserves original data types (numbers as numbers, strings as strings, etc.)
     * 
     * IMPORTANT: For ABU dates, the format is preserved because:
     * - Dates are stored as strings (not numbers)
     * - onDateInputChange converts normalized dates back to original format before storing
     * - convertValueToType for strings returns the value as-is
     * So the backend receives dates in the exact format they were in the API response
     * 
     * IMPORTANT: For ABU dates, the format is preserved because:
     * - Dates are stored as strings (not numbers)
     * - onDateInputChange converts normalized dates back to original format before storing
     * - convertValueToType for strings returns the value as-is
     * So the backend receives dates in the exact format they were in the API response
     */
    private cleanupRecordData(record: any): any {
        const cleaned: any = {};
        
        for (const key in record) {
            if (record.hasOwnProperty(key)) {
                const value = record[key];
                const dataType = this.fieldTypeMap[key] || 'string';
                
                // Convert value to the appropriate type
                cleaned[key] = this.convertValueToType(value, dataType);
            }
        }
        
        return cleaned;
    }

    /**
     * Convert a value to its target data type
     * Handles empty strings, null, and type conversion for int, float, and string
     */
    private convertValueToType(value: any, targetType: string): any {
        // Strip '-' display symbol before processing
        if (value === '-') {
            value = null;
        }
        
        // Handle empty/null values
        if (value === '' || value === null || value === undefined) {
            return null;
        }

        switch (targetType) {
            case 'int':
                // Convert to integer
                if (typeof value === 'string') {
                    const numValue = parseInt(value, 10);
                    return isNaN(numValue) ? null : numValue;
                }
                if (typeof value === 'number') {
                    return Number.isInteger(value) ? value : Math.floor(value);
                }
                return null;

            case 'float':
                // Convert to float
                if (typeof value === 'string') {
                    const numValue = parseFloat(value);
                    return isNaN(numValue) ? null : numValue;
                }
                if (typeof value === 'number') {
                    return value;
                }
                return null;

            case 'string':
            default:
                // Convert any value to string
                if (typeof value === 'string') {
                    return value;
                }
                if (value === null || value === undefined) {
                    return null;
                }
                return String(value);
        }
    }
    
    public addNewRecord(): void {
        if (!this.schemaDataSections || this.schemaDataSections.length === 0) {
            this.notificationService.error('Cannot add record: No schema data loaded');
            return;
        }
        
        // Create a new empty record based on ALL possible fields (this.displayKeys)
        const newRecord: any = {};
        
        // Initialize all fields with appropriate type-aware default values
        // Use this.displayKeys (global) to include all possible fields, not just those in first section
        this.displayKeys.forEach((key: string) => {
            if (this.isFieldReadOnly(key)) {
                // Auto-fill read-only identifier fields
                newRecord[key] = this.getAutoFillValueForReadOnlyField(key);
            } else {
                // Initialize with type-aware default value
                const dataType = this.fieldTypeMap[key] || 'string';
                newRecord[key] = this.getDefaultValueForType(dataType);
            }
        });
        
        // Create a new section (record is identified by its array index)
        const newSection = {
            index: this.schemaDataSections.length,
            originalData: { ...newRecord },
            editableData: { ...newRecord },
            displayKeys: this.displayKeys  // Use global displayKeys to include all possible fields
        };
        
        // Add the new section to the array
        this.schemaDataSections.push(newSection);
        
        // Update edit mode and change tracking arrays
        this.sectionEditMode.push(true);  // Automatically enable edit mode for new record
        this.sectionHasChanges.push(true);  // Mark as changed since it's a new record
        
        this.notificationService.success('New record added. Please fill in the fields and save.');
        
        // Update navigation guard flag
        this.updateNavigationGuard();
        
        // Update navigation guard flag
        this.updateNavigationGuard();
    }

    /**
     * Get default value for a data type
     * Int/float default to null, strings to empty string
     */
    private getDefaultValueForType(dataType: string): any {
        switch (dataType) {
            case 'int':
            case 'float':
                return null;  // Will be null in payload, empty in UI
            case 'string':
            default:
                return '';  // Empty string for text input
        }
    }

    private getAutoFillValueForReadOnlyField(key: string): any {
        // Use existing schema rows as source of truth for identifier fields
        const sourceValue = this.getFirstNonEmptyValueForKey(key);
        if (sourceValue !== '') {
            return sourceValue;
        }

        // Fallback for well name/API number style keys
        const normalizedKey = this.normalizeFieldKey(key);
        if (normalizedKey === 'wellname' || normalizedKey === 'apinumber') {
            return this.wellName || '';
        }

        return '';
    }

    private getFirstNonEmptyValueForKey(key: string): any {
        const normalizedTarget = this.normalizeFieldKey(key);

        for (const section of this.schemaDataSections) {
            const data = section?.editableData || section?.originalData || {};
            const matchingKey = Object.keys(data).find(existingKey =>
                this.normalizeFieldKey(existingKey) === normalizedTarget
            );

            if (!matchingKey) {
                continue;
            }

            const value = data[matchingKey];
            if (value !== null && value !== undefined && String(value).trim() !== '') {
                return value;
            }
        }

        return '';
    }

    private normalizeFieldKey(key: string): string {
        return (key || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    }
    
    public hasAnyChanges(): boolean {
        return this.sectionHasChanges.some(changed => changed);
    }
    
    public isAnyEditModeActive(): boolean {
        return this.sectionEditMode.some(editing => editing);
    }
    
    public fieldHasChanges(sectionIndex: number, key: string): boolean {
        const section = this.schemaDataSections[sectionIndex];
        if (!section) return false;
        
        const originalValue = section.originalData[key];
        const currentValue = section.editableData[key];
        
        // Handle null/undefined/empty comparisons
        const originalIsEmpty = originalValue === null || originalValue === undefined || originalValue === '';
        const currentIsEmpty = currentValue === null || currentValue === undefined || currentValue === '';
        
        // If both are empty, no changes
        if (originalIsEmpty && currentIsEmpty) {
            return false;
        }
        
        // If one is empty and the other isn't, there are changes
        if (originalIsEmpty !== currentIsEmpty) {
            return true;
        }
        
        // Compare actual values respecting the data type
        const dataType = this.fieldTypeMap[key] || 'string';
        
        if (dataType === 'int' || dataType === 'float') {
            // For numeric types, compare numeric values
            const originalNum = typeof originalValue === 'number' ? originalValue : parseFloat(originalValue);
            const currentNum = typeof currentValue === 'number' ? currentValue : parseFloat(currentValue);
            return originalNum !== currentNum;
        }
        
        // For strings and other types, compare as strings
        return String(originalValue) !== String(currentValue);
    }
    
    public updateSectionField(sectionIndex: number, key: string, value: any): void {
        const section = this.schemaDataSections[sectionIndex];
        if (!section) return;
        
        // Strip '-' display symbol from input before processing
        if (value === '-') {
            value = '';
        }
        
        // Convert value to the appropriate type before storing
        const dataType = this.fieldTypeMap[key] || 'string';
        const convertedValue = this.convertValueToType(value, dataType);
        
        // Update the editable data with converted value
        section.editableData[key] = convertedValue;
        
        // Check if this section has any changes
        // Use this.displayKeys (global) instead of section.displayKeys (per-record)
        // so that newly added fields that didn't exist in the original record are also detected as changes
        const hasChanges = this.displayKeys.some((k: string) => this.fieldHasChanges(sectionIndex, k));
        this.sectionHasChanges[sectionIndex] = hasChanges;
    }
    
    public undoFieldChanges(sectionIndex: number, key: string): void {
        const section = this.schemaDataSections[sectionIndex];
        if (!section) return;
        
        // Revert to original value
        section.editableData[key] = section.originalData[key];
        
        // Check if this section still has any changes
        // Use this.displayKeys (global) instead of section.displayKeys (per-record)
        // to detect changes in newly added fields that didn't exist in the original record
        const hasChanges = this.displayKeys.some((k: string) => this.fieldHasChanges(sectionIndex, k));
        this.sectionHasChanges[sectionIndex] = hasChanges;
        
        // Update navigation guard flag
        this.updateNavigationGuard();
        
        // Update navigation guard flag
        this.updateNavigationGuard();
    }
    
    public hasImage(section: any, key: string): boolean {
        const imagePathKey = `${key}_image_path`;
        const data = section.originalData || section;
        const imageUrl = data[imagePathKey];
        return typeof imageUrl === 'string' && imageUrl.startsWith('http');
    }
    
    /**
     * Check if a field actually exists in this section's data
     * Returns false for fields that don't exist (should show as disabled '-')
     */
    public fieldExistsInSection(section: any, key: string): boolean {
        // Check if the key exists in either editableData or originalData
        const existsInEditable = section.editableData && section.editableData.hasOwnProperty(key);
        const existsInOriginal = section.originalData && section.originalData.hasOwnProperty(key);
        return existsInEditable || existsInOriginal;
    }
    
    public onCellClick(section: any, key: string): void {
        const imagePathKey = `${key}_image_path`;
        const data = section.originalData || section;
        const imageUrl = data[imagePathKey];

        if (typeof imageUrl !== 'string' || !imageUrl.startsWith('http')) {
            return;
        }

        // Extract the blob path after the container name from the full Azure Blob URL
        // e.g. https://<account>.blob.core.windows.net/<container>/wells_output_.../page_8.png
        // -> wells_output_.../page_8.png
        let blobPath: string;
        try {
            const urlObj = new URL(imageUrl);
            // pathname starts with /<container>/<path>, strip the leading /<container>/
            const pathParts = urlObj.pathname.split('/');
            // pathParts[0] = '', pathParts[1] = container name, rest = blob path
            blobPath = pathParts.slice(2).join('/');
        } catch {
            blobPath = imageUrl;
        }

        this.loadingImage = true;
        this.selectedImageUrl = '';
        this.previewError = '';
        this.previewDocumentType = 'image';
        this.previewDocumentTitle = `${this.getDisplayLabel(key)} - ${this.wellName}`;

        this.httpService.getSasUrl(blobPath).subscribe({
            next: (response: any) => {
                const sasUrl = response?.sas_url || response?.url || response;
                if (typeof sasUrl === 'string' && sasUrl.startsWith('http')) {
                    this.selectedImageUrl = sasUrl;
                    this.loadingImage = false;
                } else {
                    this.previewError = 'Could not load image preview';
                    this.loadingImage = false;
                }
            },
            error: (err: any) => {
                console.error('[onCellClick] Error fetching SAS URL:', err);
                this.previewError = 'Failed to load image preview';
                this.loadingImage = false;
            }
        });
    }
    
    public closePreview(): void {
        this.selectedImageUrl = '';
        this.previewError = '';
    }
    
    public expandImage(): void {
        if (this.selectedImageUrl) {
            window.open(this.selectedImageUrl, '_blank');
        }
    }
    
    public zoomIn(): void {
        if (this.imageZoomLevel < this.maxZoom) {
            this.imageZoomLevel += 10;
        }
    }
    
    public zoomOut(): void {
        if (this.imageZoomLevel > this.minZoom) {
            this.imageZoomLevel -= 10;
        }
    }
    
    public resetZoom(): void {
        this.imageZoomLevel = 100;
    }
    
    public onImageLoad(event: any): void {
        // Handle image load
    }
    
    public toggleLogsDisplay(): void {
        this.showLogsTable = !this.showLogsTable;
        if (this.showLogsTable) {
            this.loadRecentSuggestionsLogs();
        }
    }
    
    public loadRecentSuggestionsLogs(): void {
        if (!this.wellName || !this.selectedSchemaApiName) {
            console.warn('Cannot load logs: missing well name or schema type');
            return;
        }
        
        this.loadingLogs = true;
        this.httpService.getEngineerSuggestionsLogs(this.wellName, this.selectedSchemaApiName).subscribe({
            next: (response: any) => {
                console.log('Logs loaded successfully:', response);
                
                // Handle different response formats and flatten nested arrays
                let logsArray: any[] = [];
                
                if (response && response.logs && Array.isArray(response.logs)) {
                    logsArray = response.logs;
                } else if (Array.isArray(response)) {
                    logsArray = response;
                }
                
                // Flatten nested arrays (handle [[{...}]] structure)
                const flattenedLogs: any[] = [];
                for (const item of logsArray) {
                    if (Array.isArray(item)) {
                        // If item is an array, flatten it
                        flattenedLogs.push(...item);
                    } else if (item && typeof item === 'object') {
                        // If item is an object (log entry), add it directly
                        flattenedLogs.push(item);
                    }
                }
                
                this.suggestionsLogs = flattenedLogs;
                this.loadingLogs = false;
                
                if (this.suggestionsLogs.length === 0) {
                    this.notificationService.info('No logs found for this schema');
                }
            },
            error: (err: any) => {
                console.error('Error loading logs:', err);
                this.loadingLogs = false;
                this.suggestionsLogs = [];
                this.notificationService.error('Failed to load logs: ' + (err.error?.detail || err.message || 'Unknown error'));
            }
        });
    }
    
    public formatTimestamp(timestamp: string): string {
        if (!timestamp) {
            return '-';
        }
        
        try {
            const date = new Date(timestamp);
            
            // Check if date is valid
            if (isNaN(date.getTime())) {
                return timestamp;
            }
            
            // Format: Feb 17, 2026, 4:04 PM
            return date.toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
        } catch (error) {
            console.error('Error formatting timestamp:', error);
            return timestamp;
        }
    }
    
    public formatLogValue(value: any): string {
        return value ? String(value) : '-';
    }
    
    // Notification methods
    private showNotification(message: string, type: 'success' | 'error' | 'info' = 'info'): void {
        this.error = message;
        this.notificationType = type;
        
        // Auto-hide after 5 seconds
        setTimeout(() => {
            this.error = '';
        }, 5000);
    }
    
    public cancelConfirmation(): void {
        this.showDeleteConfirmation = false;
        this.recordToDelete = -1;  // Reset deletion index
    }
    
    public confirmAction(): void {
        if (this.showDeleteConfirmation) {
            this.showDeleteConfirmation = false;
            this.performDelete();
        }
    }
    
    private performDelete(): void {
        if (this.recordToDelete < 0 || this.recordToDelete >= this.schemaDataSections.length) {
            this.notificationService.error('Invalid record index');
            this.recordToDelete = -1;
            return;
        }
        
        const section = this.schemaDataSections[this.recordToDelete];
        // Record is identified by its index position in the array
        
        if (!section) {
            this.notificationService.error('Cannot delete: Record not found');
            this.recordToDelete = -1;
            return;
        }
        
        // Start saving indicator
        this.isSaving = true;
        
        // Get user ID for the delete operation
        this.appSettingsService.getSettings().then(settings => {
            const userId = settings?.user?.username || settings?.user?.email || 'unknown';

            // Persist deletion through suggestions endpoint with updated full records list
            // This includes all remaining records (excluding the deleted one)
            // Applies to both newly added and existing records
            const updatedRecords = this.schemaDataSections
                .filter((_, index) => index !== this.recordToDelete)
                .map(section => section.editableData);

            this.httpService.saveEngineerSuggestions(
                this.wellName,
                this.selectedSchemaApiName,
                userId,
                updatedRecords
            ).subscribe({
                next: (response: any) => {
                    console.log('Record deleted successfully:', response);
                    
                    // Remove from local array
                    this.schemaDataSections.splice(this.recordToDelete, 1);
                    this.sectionEditMode.splice(this.recordToDelete, 1);
                    this.sectionHasChanges.splice(this.recordToDelete, 1);
                    
                    // Re-index remaining sections
                    this.schemaDataSections.forEach((sect, idx) => {
                        sect.index = idx;
                    });

                    // Reset change/edit state after successful persistence
                    this.sectionHasChanges = new Array(this.schemaDataSections.length).fill(false);
                    this.sectionEditMode = new Array(this.schemaDataSections.length).fill(false);
                    
                    this.isSaving = false;
                    this.recordToDelete = -1;
                    
                    // Update navigation guard flag
                    this.updateNavigationGuard();
                    
                    
                    // Update navigation guard flag
                    this.updateNavigationGuard();
                    
                    this.notificationService.success('Record deleted successfully');
                },
                error: (err: any) => {
                    console.error('Error deleting record:', err);
                    this.isSaving = false;
                    this.recordToDelete = -1;
                    this.notificationService.error('Failed to delete record: ' + (err.error?.detail || err.message || 'Unknown error'));
                }
            });
        }).catch(err => {
            console.error('Error getting user settings:', err);
            this.isSaving = false;
            this.recordToDelete = -1;
            this.notificationService.error('Failed to get user settings');
        });
    }
    
    public cancelEditCancelConfirmation(): void {
        this.showEditCancelConfirmation = false;
        this.sectionIndexToCancel = -1;
    }
    
    public confirmEditCancel(): void {
        // Revert the editable data back to original data for the section being cancelled
        if (this.sectionIndexToCancel >= 0 && this.sectionIndexToCancel < this.schemaDataSections.length) {
            const section = this.schemaDataSections[this.sectionIndexToCancel];
            // Revert all fields in editableData back to originalData
            section.editableData = { ...section.originalData };
            // Mark as no changes
            this.sectionHasChanges[this.sectionIndexToCancel] = false;
            // Close edit mode
            this.sectionEditMode[this.sectionIndexToCancel] = false;
        }
        
        // Reset index
        // Reset index
        this.sectionIndexToCancel = -1;
        
        // Update navigation guard flag
        this.updateNavigationGuard();
        
        // Update navigation guard flag
        this.updateNavigationGuard();
    }

    /**
     * Get the field validation warning message for a specific schema
     * Returns the message if validation warning should be shown, empty string otherwise
     */
    public getFieldValidationMessage(schemaName: string): string {
        const messages: { [key: string]: string } = {
            'Well Header': 'Please ensure <b>Comments</b> column is populated before proceeding to next step as it is a User-Input datafield.',
            'Casing': 'Please ensure <b>Comments</b> column is populated before proceeding to next step as it is a User-Input datafield.',
            'Casing Components': 'Please ensure <b>Component ID</b> column is populated before proceeding to next step as it is a User-Input datafield.',
            'Cement Stage': 'Please ensure <b>Comments</b>, <b>Top Depth</b> and <b>Bottom Depth</b> columns are populated before proceeding to next step as these are User-Input datafields.',
            'Perforation': 'Please ensure <b>Perforation Top Depth</b> and <b>Perforation Bottom Depth</b> columns are populated before proceeding to next step as these are User-Input datafields.',
            'Other in Hole': 'Please ensure <b>Nominal OD</b>, <b>Top Depth</b> and <b>Bottom Depth</b> columns are populated before proceeding to next step as these are User-Input datafields.'
        };
        
        return messages[schemaName] || '';
    }


    public onBulkIngestClick(): void {
        // Open Fill Comments popup in bulk ingest context
        // This allows user to review/modify fields before bulk ingest
        this.fillCommentsForBulkIngest = true;
        this.openFillCommentsPopup();
    }

    /**
     * Proceed with bulk ingest after confirmation
     */
    private proceedWithBulkIngest(): void {
        // Clear stages_completed to indicate fresh bulk ingest flow
        let stagesCompleted: string[] = [];
        this.tabNavigationService.setStagesCompleted(stagesCompleted);
        
        // Navigate to well prepare page
        this.navigateToPrepare(this.wellName, this.bu);
    }

    /**
     * Navigate to the well prepare page
     */
    private navigateToPrepare(wellIda: string, bu: string): void {
        console.log('[Bulk Ingest] Navigating to prepare page:', { wellIda, bu });

        // Navigate to prepare page
        this.router.navigate(['/wells', wellIda, 'prepare']).then(
            success => {
                console.log('[Bulk Ingest] Navigation successful:', success);
            },
            error => {
                console.error('[Bulk Ingest] Navigation failed:', error);
                this.notificationService.error('Failed to navigate to prepare page');
            }
        );
    }

    public openFillCommentsPopup(): void {
        this.showFillCommentsPopup = true;
    }

    public closeFillCommentsPopup(): void {
        this.showFillCommentsPopup = false;
        this.fillCommentsForBulkIngest = false;
    }

    public onFillCommentsSaved(): void {
        this.showFillCommentsPopup = false;
        
        // If opened from bulk ingest context, proceed with bulk ingest confirmation
        if (this.fillCommentsForBulkIngest) {
            this.fillCommentsForBulkIngest = false;
            // Add delay to allow snackbar notification to be visible before showing next dialog
            setTimeout(() => {
                this.confirmationService.show({
                    title: 'Prepare Well for Wellview',
                    message: `You are about to push the well ${this.wellName} to Wellview by automatically processing all schemas.`,
                    warningText: 'This action cannot be reversed.',
                    confirmText: 'Proceed',
                    cancelText: 'Cancel',
                    type: 'info',
                    confirmButtonType: 'save',
                    confirmCallback: () => this.proceedWithBulkIngest(),
                    cancelCallback: () => {}
                });
            }, 2500);
        } else {
            // Standard flow: reload the schema data
            if (this.selectedSchemaApiName) {
                this.loadSchemaData(this.selectedSchemaApiName);
            }
        }
    }
    
    canDeactivate(): boolean {
        // Check if there are unsaved changes
        const hasUnsavedChanges = this.hasAnyChanges() || this.isAnyEditModeActive();
        
        if (hasUnsavedChanges) {
            // Set flag to trigger confirmation dialog in nav component
            sessionStorage.setItem('__allowNavigate__', 'false');
            return false; // Prevent immediate navigation
        }
        
        // Clear flag if no unsaved changes
        sessionStorage.removeItem('__allowNavigate__');
        return true; // Allow navigation
    }
}

